/**
 * ProductsCatalog (/products) — the product list. Each card shows a product on a
 * wall with a carousel of variations (grid shape × B&W); tapping a variation opens
 * the scenery (WallProduct) preloaded with that config.
 *
 * No size tabs yet — organization/filtering ("our way") is a later pass.
 */
import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { discountConfigState } from "@atoms/priceCalc";
import useA11yWidgetAlign from "../../../utils/useA11yWidgetAlign";
import { PRODUCTS, productPrice } from "./products";
import WallThumb from "./WallThumb";
import "./style.css";

function Carousel({ product, onPick, showRuler }) {
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);
  const many = product.variations.length > 1;
  const onScroll = () => {
    const el = ref.current;
    if (!el || !el.clientWidth) return;
    setIdx(Math.min(product.variations.length - 1, Math.abs(Math.round(el.scrollLeft / el.clientWidth))));
  };
  // Arrow nav (desktop): scroll ONLY the carousel container (scrollIntoView would
  // also scroll the page horizontally and shift the whole grid).
  const go = (e, delta) => {
    e.stopPropagation();
    const el = ref.current;
    if (!el || !el.clientWidth) return;
    const cur = Math.abs(Math.round(el.scrollLeft / el.clientWidth));
    const i = Math.max(0, Math.min(product.variations.length - 1, cur + delta));
    if (i === cur) return;
    el.scrollBy({ left: -delta * el.clientWidth, behavior: "smooth" });
    setIdx(i);
  };
  return (
    <div className="pcat-carousel">
      <div className="pcat-slides" ref={ref} onScroll={onScroll}>
        {product.variations.map((v, i) => (
          <button type="button" className="pcat-slide" key={i} onClick={() => onPick(product, v)} aria-label={`${product.name} — גרסה ${i + 1}`}>
            <WallThumb frame={v.frame || product.frame} count={product.count} cols={v.cols} bw={v.bw} scene={v.scene} rows={v.rows} brick={v.brick} samples={product.id} showRuler={showRuler} dims={product.dims} />
          </button>
        ))}
      </div>
      {product.badge ? <span className="pcat-badge">{product.badge}</span> : null}
      {many ? (
        <>
          <button type="button" className="pcat-arrow start" aria-label="הקודם" onClick={(e) => go(e, -1)}>
            <svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" /></svg>
          </button>
          <button type="button" className="pcat-arrow end" aria-label="הבא" onClick={(e) => go(e, 1)}>
            <svg viewBox="0 0 24 24"><path d="M15 6l-6 6 6 6" /></svg>
          </button>
          <div className="pcat-dots">
            {product.variations.map((_, i) => <span key={i} className={i === idx ? "on" : ""} />)}
          </div>
        </>
      ) : null}
    </div>
  );
}

// One catalog card: the variation carousel + meta. Tapping the dimension toggles a
// size ruler over the frame area (instead of navigating); the discount tag mirrors the
// homepage promo (emoji + name), shown only while a promo is active.
function ProductCard({ product, onPick, discountConfig }) {
  const [ruler, setRuler] = useState(false);
  const pr = productPrice(product.count);
  const hasPromo = !!(discountConfig && discountConfig.promotionName);
  const open = () => onPick(product, product.variations[0]);
  return (
    <article className="pcat-card">
      <Carousel product={product} onPick={onPick} showRuler={ruler} />
      <div className="pcat-meta" onClick={open} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter") open(); }}>
        <div className="pcat-meta-main">
          <h3 className="pcat-name">{product.name}</h3>
          <div className="pcat-price">
            <span className="from">₪{pr.from}</span>
            {pr.was ? <span className="was">₪{pr.was}</span> : null}
          </div>
        </div>
        <div className="pcat-meta-end">
          <button type="button" className={`pcat-dims${ruler ? " on" : ""}`} aria-pressed={ruler}
            aria-label={ruler ? "הסתרת מידות" : "הצגת מידות"}
            onClick={(e) => { e.stopPropagation(); setRuler((v) => !v); }}>
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M9 3H3v6M15 21h6v-6M21 3l-7 7M3 21l7-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <span>{product.dims}</span>
          </button>
          {hasPromo ? <span className="pcat-promo">{discountConfig.emoji} מבצעי {discountConfig.promotionName}</span> : null}
        </div>
      </div>
    </article>
  );
}

export default function ProductsCatalog() {
  const navigate = useNavigate();
  const discountConfig = useRecoilValue(discountConfigState);
  useA11yWidgetAlign();

  const goProduct = useCallback((product, variation) => {
    try { localStorage.setItem("frameSelected", (variation && variation.frame) || product.frame); } catch (e) { /* ignore */ }
    const q = new URLSearchParams();
    if (product && product.id) q.set("p", product.id); // authoritative product requirement (the wall derives the name from this — keeps Hebrew out of the URL)
    if (variation && variation.bw) q.set("bw", "1");
    if (variation && variation.scene) q.set("scene", variation.scene);
    if (variation && variation.cols) q.set("cols", variation.cols);
    if (variation && variation.rows) q.set("rows", variation.rows.join("-")); // diamond etc.
    if (variation && variation.brick) q.set("brick", variation.brick.join("-")); // staggered brick rows
    const qs = q.toString();
    navigate(`/products/${product.count}-frames${qs ? "?" + qs : ""}`);
  }, [navigate]);

  return (
    <div className="pcat">
      <div className="pcat-list">
        {PRODUCTS.map((p) => <ProductCard key={p.id} product={p} onPick={goProduct} discountConfig={discountConfig} />)}
      </div>
    </div>
  );
}
